import { EN_US, RU_RU, LocaleConfig } from '../config/Translations';
import { SupportedLanguage } from '../data/ProfileState';
import { getSharedProfileRuntime, ProfileRuntime } from './ProfileRuntime';
import { createLogger } from '../utils/Logger';

export class LocalizationManager {
    private static instance: LocalizationManager | undefined;
    private currentLocale: LocaleConfig;
    private languageCode: SupportedLanguage;
    private readonly logger = createLogger('LocalizationManager');

    private constructor(private readonly profileRuntime: ProfileRuntime = getSharedProfileRuntime()) {
        const profileLanguage = this.profileRuntime.getProfileSnapshot().profile?.settings.language;
        this.languageCode = profileLanguage ?? this.detectBrowserLanguage();
        this.logger.info('constructor:language-resolved', { profileLanguage, languageCode: this.languageCode });
        this.currentLocale = this.languageCode === 'ru' ? RU_RU : EN_US;
    }

    public static getInstance(profileRuntime?: ProfileRuntime): LocalizationManager {
        if (!LocalizationManager.instance || (profileRuntime && LocalizationManager.instance.profileRuntime !== profileRuntime)) {
            LocalizationManager.instance = new LocalizationManager(profileRuntime);
        }
        return LocalizationManager.instance;
    }

    public getText(key: string, params?: (string | number)[]): string {
        const template = this.currentLocale[key];

        if (!template) {
            console.warn(`LocalizationManager: Missing key '${key}' for locale '${this.languageCode}'`);
            return key; // Fallback to key
        }

        if (params && params.length > 0) {
            return params.reduce<string>((acc, param, index) => {
                return acc.replace(`{${index}}`, String(param));
            }, template);
        }

        return template;
    }

    public getLanguage(): SupportedLanguage {
        return this.languageCode;
    }

    public setLanguage(language: SupportedLanguage, persist: boolean = true): void {
        this.languageCode = language;
        this.currentLocale = language === 'ru' ? RU_RU : EN_US;
        if (persist) {
            const result = this.profileRuntime.updateSettings({ language });
            if (!result.ok) {
                this.logger.warn('setLanguage:persist-failed', { language, error: result.error });
            }
        }
        this.logger.info('setLanguage:complete', { language, persist });
    }

    public static resetForTests(): void {
        LocalizationManager.instance = undefined;
    }

    private detectBrowserLanguage(): SupportedLanguage {
        const browserLang = globalThis.navigator?.language || 'en';
        return browserLang.toLowerCase().startsWith('ru') ? 'ru' : 'en';
    }
}
